import React, { useState, useEffect } from 'react';
import { XGrid, RowsProp, ColDef, CellParams } from '@material-ui/x-grid';
import { makeStyles } from '@material-ui/core/styles';
import { Redirect, Route, useHistory } from 'react-router-dom';
import { Button, CardContent, Card, Grid, Dialog, DialogTitle, DialogContent, TextField, DialogActions } from '@material-ui/core';
import Strapi from '../../services/strapi';
import { Role, useAuthentication } from '../../contexts/Authentication';

interface ListCategoryProps {
	refresh: boolean,
	collection_name: string,
	content_name: string,
	action: (category: any) => void,
	edit?: boolean,
	create?: boolean,
	delete?: boolean
}

type Right = "view" | "edit" | "create" | "remove";

type Rights = Right[];
interface EditCategoryProps {
	edit?: boolean,
	create?: boolean,
	delete?: boolean,
	collection_name: string,
	content_name: string,
	display_name: string
}

const pageSize = 20;

const useStyles = makeStyles((theme) => ({
	container: {
    	display: 'flex',
    	flex: 1,
    	flexDirection: 'column'
  	},
	grid: {
		display: 'flex',
		flexDirection: "column",
		height: 'calc(85vh)',
		width: '100%',
		marginTop: '2vh'
	},
	buttons: {
		display: 'flex',
		justifyContent: 'space-around',
		flex: 1
	},
	search: {
		marginBottom: '1vh'
	},
	searchCard: {
		display: 'flex',
		flex: 1
	},
	newButton: {
		display: 'flex',
		flex: 1
	},
	searchText: {
		display: 'flex',
		flex: 9,
		marginRight: '2vh'
	}
}))

const ListCategory = (props: ListCategoryProps) => {
	const [articles, setArticle] = useState<any[]>([]);
	const [page, setPage] = useState<number>(1);
	const [loadling, setLoading] = useState<boolean>(false);
	const [rowCount, setRowCout] = useState<number>(0);
	const [rows, setRows] = useState<RowsProp>([]);
	const [search, setSearch] = useState<string>("");
	const classes = useStyles();

	useEffect(() => {
		Strapi.get(props.collection_name + "/count", {
			[`${props.content_name}_contains`]: search,
		}).then((r) => {
			setRowCout(r);
		})
	}, [props.collection_name, props.content_name, search, props.refresh])

	useEffect(() => {
		const params: any = {
			[`${props.content_name}_contains`]: search,
			_limit: pageSize,
			_start: (page - 1) * pageSize,
		};
		setLoading(true);
		Strapi.get(props.collection_name, params).then((r) => {
			setArticle(r);
			setLoading(false);
		});
	}, [page, props.collection_name, props.content_name, search, props.refresh]);

	useEffect(() => {
		const temp_rows: RowsProp = articles.map((e, i) => ({
			index: i,
			id: e.id,
			slug: e.slug,
			name: e[props.content_name],
			action: e.slug,
		}))
		setRows(temp_rows);
	}, [articles, props.content_name])


	const columns: ColDef[] = [
		{ field: 'name', headerName: 'Nom', width: 300 },
		{
			field: 'action', headerName: 'Actions', width: 300, renderCell: (params: CellParams) => {
				return <>
					{props.edit && <Button onClick={() => {
						props.action(params.data);
					}}>Modifier</Button>}
				</>
			}
		}
	];
	return (
		<div className={classes.container}>
			<div className={classes.search}>
				<Card>
					<CardContent className={classes.searchCard}>
						<TextField placeholder="Rechercher" value={search} variant="outlined" fullWidth className={classes.searchText} onChange={e => {
							setSearch(e.target.value);
						}}></TextField>
						{props.create && <Button variant="contained" className={classes.newButton} onClick={() => props.action(undefined)}>Nouveau</Button>}
					</CardContent>
				</Card>
			</div>
			<div className={classes.container}>
				<XGrid
					pagination
					rows={rows}
					rowCount={rowCount}
					pageSize={pageSize}
					columns={columns}
					paginationMode="server"
					loading={loadling}
					onPageChange={(params) => {
						setPage(params.page)
					}}
				/>
			</div>
		</div>
	);
}

interface EditCategoryPopupProps {
	open: boolean,
	onclose: () => void,
	collection_name: string,
	article: any | undefined,
	valueName: string,
	displayName: string
}

const EditCategoryPopup = (props: EditCategoryPopupProps) => {
	const [value, setValue] = useState<string>("");
	
	useEffect(() => {
		if (props.article !== undefined) {
			const id = ["themes", "mot-cles"].includes(props.collection_name) ? props.article.slug : props.article.id;
			Strapi.get(props.collection_name + "/" + id).then((r) => {
				setValue(r[props.valueName]);
			})
		} else {
			setValue("");
		}
	}, [props.collection_name, props.article]);
	
	return (
		<Dialog open={props.open} onClose={props.onclose}>
			<DialogTitle>{props.displayName}</DialogTitle>
			<DialogContent>
				<TextField label="name" value={value} onChange={(e) => {
					setValue(e.target.value);
				}}/>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => {
					const data: any = {};
					data[props.valueName] = value;
					if (props.article === undefined) {
						Strapi.post(props.collection_name, {}, data).then((r) => {
							setValue("");
							props.onclose();
						});
					} else {
						Strapi.put(props.collection_name + "/" + props.article.id, {}, data)
						.then(props.onclose);
					}
				}}>{props.article === undefined ? 'Créer' : 'Mettre à jour'}</Button>
			</DialogActions>
		</Dialog>
	);
}

const EditCategory = (props: EditCategoryProps) => {
	const classes = useStyles();
	const [open, setOpen] = useState<boolean>(false);
	const [currentCategory, setCurrentCategory] = useState<any>(undefined);
	const [refresh, setRefresh] = useState<boolean>(false);

	const update = () => {
		setRefresh(v => !v);
	}

	return (
		<Grid container>
			<EditCategoryPopup
				open={open}
				onclose={() => {
					setOpen(false);
					update()
				}}
				collection_name={props.collection_name}
				article={currentCategory}
				valueName={props.content_name}
				displayName={props.display_name}
			/>

			<div className={classes.grid}>
				<ListCategory {...props} refresh={refresh} action={(id) => {
					setCurrentCategory(id);
					setOpen(true);
				}}/>
			</div>
		</Grid>
	);
};

const getAllowedTypes = (roles: Role[]) => {
	const types = [
	  {
		name: "Themes",
		collection: "themes",
		view: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		edit: [Role.Administrateur],
		delete: [Role.Administrateur],
		create: [Role.Administrateur],
		content_name: "name",
	  },
	  {
		name: "Mot Clé",
		collection: "mot-cles",
		view: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		edit: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		delete: [],
		create: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		content_name: "content",
	},
	{
		name: "Type de rdv",
		collection: "rdv-types",
		view: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		edit: [Role.Administrateur],
		delete: [Role.Administrateur],
		create: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		content_name: "name"
	  },
	  {
		name: "Podcast",
		collection: "podcast-types",
		view: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		edit: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		delete: [Role.Administrateur],
		create: [Role.Administrateur, Role.Editorial1, Role.Editorial2, Role.Redacteur, Role.RedacteurChef, Role.RedacteurJunior],
		content_name: "name",
	  },
	];
	return types.filter(e => roles.some(r => e.view.includes(r))) 
  }

function is_allowed(roles: Role[], allowed_roles: Role[]) {
	return roles.some(e => allowed_roles.includes(e));
}

const Category = () => {
	const classes = useStyles();
	const history = useHistory();
	const auth = useAuthentication();
	const path = history.location.pathname.split("/");
	const allowedTypes = getAllowedTypes(auth.role);

	return (
		<div className={classes.container}>
			<Card>
				<CardContent className={classes.buttons}>
					{allowedTypes.map(e => (
						<Button key={e.collection} size="large" variant={path[path.length - 1] === e.collection ? "contained" : "outlined"} onClick={() => {
							history.push(`/category/${e.collection}`);
						}}>{e.name}</Button>
					))}
				</CardContent>
			</Card>
			<Redirect exact from="/category" to={`/category/${allowedTypes[0].collection}`}/>
			{allowedTypes.map(e => (
				<Route key={e.collection} path={`/category/${e.collection}`}>
					<EditCategory 
						edit={is_allowed(auth.role, e.edit)}
						delete={is_allowed(auth.role, e.delete)}
						create={is_allowed(auth.role, e.create)}
						collection_name={e.collection}
						content_name={e.content_name}
						display_name={e.name}/>
				</Route>
			))}
		</div>
	);
}

export default Category;

